import { Result } from '../errors';
import { mfsError } from '../errors/storeErrors';
import { COLLECTION_KIND_KEY, hasProperty } from '../utility';
import { isValidCollection } from './collections';
import { canUseLabelId, isValidLabelPropertyValue, LabelTracker } from './labels';
import {
  hasTraits,
  MfsBaseProperties,
  MfsCollectionProperties,
  MfsPropertyTrait,
  MfsSystemProperties,
} from './mfsProperty';
import { isMfsCollection } from './mfsTypeGuard';
import { MfsItem } from './mfsTypes';
import { MfsValue } from './mfsValue';

/** Checks whether the given item can be created by verifying its base properties. */
export function canCreateItem(item: Partial<MfsItem>, itemIdToCreate: string, itemLabels: LabelTracker): Result {
  if (hasAnySystemProperty(item as unknown as MfsItem)) {
    return Result.fail(mfsError.systemPropertiesProvided());
  }

  // verify all required, app provided properties exist.
  const validPropertiesResult = hasAllPropertiesWith(MfsPropertyTrait.App | MfsPropertyTrait.Required, item);
  if (validPropertiesResult.isOK === false) {
    return validPropertiesResult;
  }

  if (item.label === undefined || !isValidLabelPropertyValue(item.label)) {
    return Result.fail(mfsError.invalidLabel());
  }

  // ensure that labelId is not already in use by another item.
  if (item?.labelId !== undefined) {
    const canUseLabelIdResult = canUseLabelId(itemLabels, item.label, item.labelId, itemIdToCreate);
    if (canUseLabelIdResult.isOK === false) {
      return canUseLabelIdResult;
    }
  }

  // if any collection property is provided, all must exist.
  if (hasAnyCollectionProperty(item)) {
    const result = hasAllPropertiesWith(MfsPropertyTrait.CollectionRequired, item);
    return result.isOK === true && isMfsCollection(item) ? isValidCollection(item) : result;
  }

  return Result.ok();
}

/** Prevent creating collections using the wrong api. */
export function ensureNotCollection(item: Partial<MfsItem>): Result {
  if (COLLECTION_KIND_KEY in item) {
    return Result.fail(mfsError.invalidCreationMethod());
  }
  return Result.ok();
}

/**
 * Returns true if the item explicitly has any of the system properties specified
 *
 * @param item Mfs item
 */
export function hasAnySystemProperty(item: { [propertyName: string]: MfsValue }): boolean {
  return hasAnyProperty(MfsSystemProperties, item);
}

function hasAnyCollectionProperty(item: { [propertyName: string]: MfsValue }): boolean {
  return hasAnyProperty(MfsCollectionProperties, item);
}

function hasAnyProperty(ofRecord: Record<string, unknown>, item: { [propertyName: string]: MfsValue }): boolean {
  return Object.keys(ofRecord).some((key) => hasProperty(item, key));
}

function hasAllPropertiesWith(trait: MfsPropertyTrait, item: { [propertyName: string]: MfsValue }): Result {
  for (const property in MfsBaseProperties) {
    if (hasTraits(property, trait) && !hasProperty(item, property)) {
      return Result.fail(new Error(`Required property: ${property} is missing.`));
    }
  }

  return Result.ok();
}
